<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="close">
      <div class="howto" role="dialog" aria-label="How to play">
        <button class="howto__close" aria-label="Close" @click="close">✕</button>

        <div class="howto__body">
          <h3>{{ slide.title }}</h3>
          <div v-if="slide.art" class="howto__art" aria-hidden="true">
            <span v-for="(ch, i) in slide.art" :key="i" :class="{ 'is-black': ch === '#', 'is-hot': ch === '*' }">{{ ch === '#' || ch === '*' ? '' : ch }}</span>
          </div>
          <!-- eslint-disable-next-line vue/no-v-html -- content is this file's own
               constant strings below, never user input -->
          <p v-for="(line, i) in slide.lines" :key="i" class="howto__line" v-html="line" />
        </div>

        <div class="howto__nav">
          <button class="secondary" :disabled="index === 0" @click="index -= 1">Back</button>
          <span class="howto__dots">
            <i v-for="(s, i) in slides" :key="i" class="howto__dot" :class="{ 'howto__dot--on': i === index }" />
          </span>
          <button v-if="index < slides.length - 1" class="primary howto__next" @click="index += 1">Next</button>
          <button v-else class="primary howto__next" @click="close">Got it</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
// The walkthrough - the house pattern (auto-opens once, behind ? after).
// Illustrated with little grids drawn from strings; the claims are asserted
// against the engine in howToPlay.test.js so the tutorial cannot drift.
export const SLIDES = [
  {
    title: 'One puzzle a day',
    art: 'P#C#E###N#B#R#',
    lines: [
      'Every day there is a new <strong>15×15 themed crossword</strong> here — Monday easiest, Saturday hardest. Sunday is a bigger <strong>21×21</strong>, somewhere relaxed in between.',
      'The theme is a secret until you finish. Then it is revealed, with the entries that carried it.',
      'Clues are written by Claude; the fill comes from a real constructors’ word list.',
    ],
  },
  {
    title: 'Filling it in',
    art: 'C*A*T#####*#*#',
    lines: [
      '<strong>Tap a square</strong>, then type. Tap it again to switch between across and down — or tap the clue bar.',
      'The arrows beside the clue walk through the clues; on a keyboard, Tab does the same, Space flips direction.',
      'Your progress saves itself, on this device and on your account. The clock only runs while the puzzle is open.',
    ],
  },
  {
    title: 'Stuck?',
    art: '?#?#?#####?#?#',
    lines: [
      'The <strong>⋯ menu</strong> can check a square, a word or the whole puzzle: a wrong letter gets a red slash until you change it.',
      'It can also reveal a square or a word. A solve with any reveals still counts — it just isn’t a <strong>clean solve</strong>, and clean solves are what the high scores rank.',
    ],
  },
  {
    title: 'Solve together',
    art: 'W*E#T*O#G*E#T#',
    lines: [
      'From the menu, <strong>Solve together</strong> gives you a code and a link. Anyone who joins is in the same grid on their own phone; letters appear live in each person’s colour, and you can see where they are.',
      'When it is done, everyone’s time goes on the day’s board — the first thing you see after the theme.',
    ],
  },
  {
    title: 'The Giant',
    art: 'G*I*A*N*T#####',
    lines: [
      'Every season there is a <strong>Giant</strong>: a 101×101 crossword with about 3,500 clues and a big theme running all through it. It is meant to take weeks — alone, or with your group.',
      'The grid shows a window around your word and follows you as you go; drag it to look around, or tap the <strong>four squares</strong> at the top to see the whole thing and jump anywhere.',
      'A Giant keeps its own board and stays out of your daily stats.',
    ],
  },
];

export default {
  name: 'HowToPlay',
  emits: ['close'],
  data() {
    return { index: 0, slides: SLIDES };
  },
  computed: {
    slide() { return this.slides[this.index]; },
  },
  methods: {
    close() { this.$emit('close'); },
  },
};
</script>

<style lang="scss" scoped>
.howto__art {
  display: grid;
  grid-template-columns: repeat(7, 26px);
  gap: 2px;
  justify-content: center;
  margin: 0.6rem auto 0.9rem;
  span {
    width: 26px; height: 26px; background: var(--paper); color: var(--paper-ink); font-weight: 700; font-size: 0.95rem;
    display: flex; align-items: center; justify-content: center; border-radius: 2px;
    &.is-black { background: #111; }
    &.is-hot { background: var(--accent); }
  }
}
</style>
